import { useEffect } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import { useNotificationsStore, type AppNotification } from '../../stores/notifications';
import { MessageSquare, Mail, CheckSquare, X } from '../../lib/icons';

const ICONS: Record<string, React.ReactNode> = {
  message: <MessageSquare size={14} strokeWidth={1.8} />,
  thread: <Mail size={14} strokeWidth={1.8} />,
  task: <CheckSquare size={14} strokeWidth={1.8} />,
};

function timeAgo(ts: string) {
  try { return formatDistanceToNowStrict(new Date(ts), { addSuffix: true }); }
  catch { return ''; }
}

interface Props {
  onNavigate: (notif: AppNotification) => void;
  onItemClick: () => void;
}

// Content-only notification panel — embedded in TopBar's avatar dropdown.
// Marks everything read as soon as it mounts (i.e. as soon as the dropdown opens).
export default function NotificationList({ onNavigate, onItemClick }: Props) {
  const items       = useNotificationsStore((s) => s.items);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const markAllRead = useNotificationsStore((s) => s.markAllRead);
  const remove      = useNotificationsStore((s) => s.remove);
  const clear       = useNotificationsStore((s) => s.clear);

  useEffect(() => {
    if (unreadCount > 0) markAllRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleClick(notif: AppNotification) {
    onItemClick();
    onNavigate(notif);
  }

  return (
    <div>
      <div style={{
        padding: '10px 12px', borderBottom: '1px solid var(--shell-border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--shell-text)' }}>
          Notifications
        </span>
        {items.length > 0 && (
          <button
            onClick={clear}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--shell-xs)', fontSize: '0.72rem', padding: 0,
            }}
          >
            Clear all
          </button>
        )}
      </div>

      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {items.length === 0 ? (
          <div style={{
            padding: '24px 16px', textAlign: 'center',
            color: 'var(--shell-xs)', fontSize: '0.78rem',
          }}>
            No notifications
          </div>
        ) : (
          items.map((notif) => (
            <div
              key={notif.id}
              style={{
                borderBottom: '1px solid var(--shell-border)',
                display: 'flex', alignItems: 'flex-start',
                background: notif.read ? 'transparent' : 'rgba(29,158,117,.06)',
                transition: 'background .1s',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = 'var(--hover)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.background = notif.read ? 'transparent' : 'rgba(29,158,117,.06)';
              }}
            >
              <div
                onClick={() => handleClick(notif)}
                style={{
                  flex: 1, minWidth: 0, padding: '10px 12px',
                  cursor: notif.threadId || notif.taskId ? 'pointer' : 'default',
                  display: 'flex', gap: 9, alignItems: 'flex-start',
                }}
              >
                <span style={{ flexShrink: 0, marginTop: 2, color: 'var(--shell-xs)' }}>
                  {ICONS[notif.type]}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '0.78rem', fontWeight: 600,
                    color: 'var(--shell-muted)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {notif.title}
                  </div>
                  <div style={{
                    fontSize: '0.73rem', color: 'var(--shell-xs)', marginTop: 2,
                    overflow: 'hidden', display: '-webkit-box',
                    WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  }}>
                    {notif.body}
                  </div>
                  <div style={{ fontSize: '0.67rem', color: 'var(--shell-xs)', marginTop: 3, opacity: 0.6 }}>
                    {timeAgo(notif.timestamp)}
                  </div>
                </div>
              </div>

              <button
                onClick={(e) => { e.stopPropagation(); remove(notif.id); }}
                title="Dismiss"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--shell-xs)', fontSize: '0.75rem',
                  padding: '10px 10px 10px 4px', lineHeight: 1, flexShrink: 0,
                  opacity: 0.5, transition: 'opacity .1s',
                  display: 'flex', alignItems: 'center',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.5')}
              >
                <X size={12} strokeWidth={1.8} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
